import { ChangeEvent, ReactNode, useState } from 'react';
import { Panel, PanelType } from '@fluentui/react';
import {
  Button,
  Field,
  FluentProvider,
  Text,
  webLightTheme,
} from '@fluentui/react-components';
import { useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../atoms/messageBarAtoms';
import { readFileAsText } from '../utils/csvHelpers';

export interface ImportPanelProps<TResponse> {
  isOpen: boolean;
  headerText: string;
  onDismiss: () => void;
  importApi: (
    abortController: AbortController,
    csvContent: string
  ) => Promise<TResponse>;
  onImportSucceeded: (result: TResponse) => string;
  getErrorsCount?: (result: TResponse) => number;
  renderResults?: (result: TResponse) => ReactNode;
  onAfterImport?: () => void;
}

export const ImportPanel = <TResponse,>({
  isOpen,
  headerText,
  onDismiss,
  importApi,
  onImportSucceeded,
  getErrorsCount,
  renderResults,
  onAfterImport,
}: ImportPanelProps<TResponse>) => {
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);

  const [fileName, setFileName] = useState('');
  const [csvContent, setCsvContent] = useState('');
  const [result, setResult] = useState<TResponse | null>(null);

  const reset = () => {
    setFileName('');
    setCsvContent('');
    setResult(null);
  };

  const handleDismiss = () => {
    reset();
    onDismiss();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setFileName('');
      setCsvContent('');
      return;
    }
    setFileName(file.name);
    try {
      setCsvContent(await readFileAsText(file));
    } catch {
      setCsvContent('');
      setErrorMessage('Failed to read the selected CSV file.');
    }
  };

  const handleImport = async () => {
    if (!csvContent) return;

    const abortController = new AbortController();
    setIsInProgress(true);
    try {
      const response = await importApi(abortController, csvContent);
      setResult(response);
      const errorsCount = getErrorsCount?.(response) ?? 0;
      if (errorsCount > 0) {
        setErrorMessage(
          `Import finished with ${errorsCount} error(s). See panel for details.`
        );
      } else {
        setSuccessMessage(onImportSucceeded(response));
      }
      onAfterImport?.();
    } catch (error: any) {
      console.error('Error:', error);
      setErrorMessage(error.message);
    } finally {
      setIsInProgress(false);
    }
  };

  return (
    <Panel
      isOpen={isOpen}
      onDismiss={handleDismiss}
      type={PanelType.medium}
      headerText={headerText}
    >
      <FluentProvider theme={webLightTheme}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            marginTop: 16,
          }}
        >
          <Field label="CSV file">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
            />
          </Field>
          <Text size={200}>{fileName || 'No file selected'}</Text>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            <Button
              appearance="primary"
              onClick={() => {
                void handleImport();
              }}
              disabled={!csvContent}
            >
              Import
            </Button>
            <Button appearance="secondary" onClick={handleDismiss}>
              Cancel
            </Button>
          </div>
          {result && renderResults ? renderResults(result) : null}
        </div>
      </FluentProvider>
    </Panel>
  );
};

export default ImportPanel;
